// frontend/packages/business-console/src/pages/WorkflowFormSubmissionDetail.jsx
//
// Work item 4.11b - submission detail view. Operator clicks a row
// from the Customer profile Forms tab → sees what the tech filled
// in. Resolves field labels via the form's compiledSchema (which
// arrives in the same response so this page never round-trips
// twice).
//
// `/business/:slug/forms/submissions/:id`

import React, { useContext, useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Divider,
  IconButton,
  Link as MuiLink,
  Paper,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';
import ArrowBackRoundedIcon from '@mui/icons-material/ArrowBackRounded';
import AttachFileOutlinedIcon from '@mui/icons-material/AttachFileOutlined';
import CheckOutlinedIcon from '@mui/icons-material/CheckOutlined';
import BlockOutlinedIcon from '@mui/icons-material/BlockOutlined';
import PictureAsPdfOutlinedIcon from '@mui/icons-material/PictureAsPdfOutlined';
import EmailOutlinedIcon from '@mui/icons-material/EmailOutlined';
import { Link as RouterLink, useParams } from 'react-router-dom';

import { BusinessContext } from '../contexts/BusinessContext';
import {
  getWorkflowFormSubmission,
  setAttachmentScanStatus,
  renderWorkflowFormReport,
  getWorkflowFormReportDownloadUrl,
  emailWorkflowFormReport,
} from '../services/workflowFormService';

function formatTimestamp(input) {
  if (!input) return '-';
  const d = new Date(input);
  if (Number.isNaN(d.getTime())) return '-';
  return d.toLocaleString();
}

function buildLabelByPath(compiledSchema) {
  const out = {};
  const fields =
    compiledSchema && Array.isArray(compiledSchema.fields)
      ? compiledSchema.fields
      : [];
  for (const f of fields) {
    if (!f || typeof f.path !== 'string') continue;
    out[f.path] = (f.props && f.props.label) || f.path;
  }
  return out;
}

function humanizeKey(k) {
  const s = String(k)
    .replace(/[_-]+/g, ' ')
    .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
    .trim();
  return s ? s.charAt(0).toUpperCase() + s.slice(1) : String(k);
}

function renderScalar(v) {
  if (v === null || v === undefined || v === '') {
    return (
      <Typography variant="body2" sx={{ color: 'text.disabled' }}>
        (empty)
      </Typography>
    );
  }
  if (typeof v === 'boolean') {
    return (
      <Chip
        size="small"
        label={v ? 'Yes' : 'No'}
        color={v ? 'success' : 'default'}
        variant="outlined"
      />
    );
  }
  return (
    <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
      {String(v)}
    </Typography>
  );
}

// Render one object (e.g. a Repeatable row) as a label/value list.
// Nested-field labels resolve from the compiled schema via the
// `${path}[].${key}` convention, falling back to a humanized key.
function renderObject(obj, labelByPath, path) {
  const entries = Object.entries(obj);
  if (entries.length === 0) {
    return (
      <Typography variant="body2" sx={{ color: 'text.disabled' }}>
        (empty)
      </Typography>
    );
  }
  return (
    <Stack spacing={0.5}>
      {entries.map(([k, val]) => {
        const label =
          (path && labelByPath[`${path}[].${k}`]) || humanizeKey(k);
        const nested = Array.isArray(val) || (val && typeof val === 'object');
        return (
          <Stack
            key={k}
            direction={nested ? 'column' : 'row'}
            spacing={nested ? 0.25 : 1}
            sx={{ alignItems: nested ? 'stretch' : 'baseline' }}
          >
            <Typography
              variant="caption"
              sx={{ color: 'text.secondary', minWidth: 140, flexShrink: 0 }}
            >
              {label}
            </Typography>
            {nested ? renderValue(val, labelByPath, path) : renderScalar(val)}
          </Stack>
        );
      })}
    </Stack>
  );
}

function renderValue(v, labelByPath = {}, path = '') {
  if (v === null || v === undefined || v === '') {
    return (
      <Typography variant="body2" sx={{ color: 'text.disabled' }}>
        (empty)
      </Typography>
    );
  }
  if (typeof v === 'boolean') {
    return renderScalar(v);
  }
  if (Array.isArray(v)) {
    if (v.length === 0) {
      return (
        <Typography variant="body2" sx={{ color: 'text.disabled' }}>
          (none)
        </Typography>
      );
    }
    // Array of objects = Repeatable rows → numbered sub-cards instead of
    // a raw JSON dump.
    if (v.every((it) => it && typeof it === 'object' && !Array.isArray(it))) {
      return (
        <Stack spacing={1}>
          {v.map((item, idx) => (
            <Box
              key={idx}
              sx={{
                p: 1,
                border: (t) => `1px solid ${t.palette.divider}`,
                borderRadius: 1,
              }}
            >
              <Typography
                variant="caption"
                sx={{
                  color: 'text.secondary',
                  fontWeight: 700,
                  display: 'block',
                  mb: 0.5,
                }}
              >
                #{idx + 1}
              </Typography>
              {renderObject(item, labelByPath, path)}
            </Box>
          ))}
        </Stack>
      );
    }
    // Array of scalars = MultiSelect → chips.
    return (
      <Stack direction="row" spacing={0.5} sx={{ flexWrap: 'wrap', gap: 0.5 }}>
        {v.map((item, idx) => (
          <Chip
            key={idx}
            size="small"
            label={typeof item === 'object' ? JSON.stringify(item) : String(item)}
            variant="outlined"
          />
        ))}
      </Stack>
    );
  }
  if (typeof v === 'object') {
    return renderObject(v, labelByPath, path);
  }
  return renderScalar(v);
}

export default function WorkflowFormSubmissionDetail() {
  const { business } = useContext(BusinessContext);
  const { id } = useParams();
  const basePath = business?.slug ? `/business/${business.slug}` : '';

  const [submission, setSubmission] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  // 4.13 - scan-status override per attachment, keyed by `blobUrl`.
  const [overrideBusyFor, setOverrideBusyFor] = useState(null);
  const [overrideError, setOverrideError] = useState(null);
  // 4.16 - service-report download + email.
  const [reportBusy, setReportBusy] = useState(null); // 'download' | 'email' | null
  const [reportMsg, setReportMsg] = useState(null); // { severity, text }

  const handleDownloadReport = async () => {
    if (!submission) return;
    setReportMsg(null);
    setReportBusy('download');
    try {
      // Ensure a current report exists (cache-aware), then fetch a
      // short-TTL signed URL and open it.
      await renderWorkflowFormReport(submission._id);
      const { url } = await getWorkflowFormReportDownloadUrl(submission._id);
      window.open(url, '_blank', 'noopener,noreferrer');
    } catch (err) {
      setReportMsg({
        severity: 'error',
        text:
          err?.response?.data?.message ||
          err?.message ||
          "Couldn't generate the report.",
      });
    } finally {
      setReportBusy(null);
    }
  };

  const handleEmailReport = async () => {
    if (!submission) return;
    setReportMsg(null);
    setReportBusy('email');
    try {
      const res = await emailWorkflowFormReport(submission._id);
      setReportMsg({
        severity: 'success',
        text: `Report emailed to ${res?.to || 'the customer'}.`,
      });
    } catch (err) {
      setReportMsg({
        severity: 'error',
        text:
          err?.response?.data?.message ||
          err?.message ||
          "Couldn't email the report.",
      });
    } finally {
      setReportBusy(null);
    }
  };

  const handleOverride = async (att, scanStatus) => {
    if (!submission) return;
    setOverrideError(null);
    setOverrideBusyFor(att.blobUrl);
    try {
      const res = await setAttachmentScanStatus(submission._id, {
        fieldId: att.fieldId,
        blobUrl: att.blobUrl,
        scanStatus,
      });
      if (res?.submission) setSubmission(res.submission);
    } catch (err) {
      setOverrideError(
        err?.response?.data?.message ||
          err?.message ||
          "Couldn't update scan status."
      );
    } finally {
      setOverrideBusyFor(null);
    }
  };

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError(null);
      try {
        const res = await getWorkflowFormSubmission(id);
        if (!cancelled) setSubmission(res.submission);
      } catch (err) {
        if (!cancelled) {
          setError(
            err?.response?.data?.message ||
              err?.message ||
              "Couldn't load this submission."
          );
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [id]);

  if (loading) {
    return (
      <Box sx={{ textAlign: 'center', py: 8 }}>
        <CircularProgress />
      </Box>
    );
  }
  if (error || !submission) {
    return (
      <Box sx={{ p: 3, maxWidth: 720, mx: 'auto' }}>
        <Tooltip title="Back to forms">
          <IconButton
            size="small"
            component={RouterLink}
            to={`${basePath}/forms`}
            sx={{ mb: 2 }}
          >
            <ArrowBackRoundedIcon fontSize="small" />
          </IconButton>
        </Tooltip>
        <Alert severity="error">{error || 'Submission not found.'}</Alert>
      </Box>
    );
  }

  const labelByPath = buildLabelByPath(submission.formCompiledSchema);
  const valueEntries = Object.entries(submission.values || {});
  const attachments = submission.attachments || [];

  return (
    <Box sx={{ p: { xs: 2, sm: 3 }, maxWidth: 900, mx: 'auto' }}>
      <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mb: 2 }}>
        <Tooltip
          title={submission.customerId ? 'Back to customer' : 'Back to forms'}
        >
          <IconButton
            size="small"
            component={RouterLink}
            to={
              submission.customerId
                ? `${basePath}/crm/${submission.customerId}`
                : `${basePath}/forms`
            }
            sx={{ color: 'text.secondary' }}
          >
            <ArrowBackRoundedIcon fontSize="small" />
          </IconButton>
        </Tooltip>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography sx={{ fontWeight: 700 }} noWrap>
            {submission.formName || submission.formSlug || 'Workflow form'}
          </Typography>
          <Typography variant="caption" sx={{ color: 'text.secondary' }}>
            v{submission.formVersion} ·{' '}
            {formatTimestamp(submission.submittedAt)}
          </Typography>
        </Box>
        <Chip
          size="small"
          label={submission.status}
          color={submission.status === 'submitted' ? 'success' : 'default'}
          variant="outlined"
          sx={{ textTransform: 'capitalize' }}
        />
      </Stack>

      <Paper
        variant="outlined"
        sx={{ p: { xs: 1.5, sm: 2 }, borderRadius: 2, mb: 2 }}
      >
        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          spacing={1.5}
          alignItems={{ xs: 'stretch', sm: 'center' }}
        >
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography variant="body2" sx={{ fontWeight: 700 }}>
              Service report
            </Typography>
            <Typography variant="caption" sx={{ color: 'text.secondary' }}>
              Render this submission as a PDF, then download it or email it to the
              customer.
            </Typography>
          </Box>
          <Button
            size="small"
            variant="outlined"
            startIcon={<PictureAsPdfOutlinedIcon fontSize="small" />}
            onClick={handleDownloadReport}
            disabled={Boolean(reportBusy)}
          >
            {reportBusy === 'download' ? 'Preparing…' : 'Download PDF'}
          </Button>
          <Tooltip
            title={
              submission.customerId
                ? ''
                : 'Link this submission to a customer to email the report.'
            }
          >
            <span>
              <Button
                size="small"
                variant="contained"
                disableElevation
                startIcon={<EmailOutlinedIcon fontSize="small" />}
                onClick={handleEmailReport}
                disabled={Boolean(reportBusy) || !submission.customerId}
              >
                {reportBusy === 'email' ? 'Sending…' : 'Email to customer'}
              </Button>
            </span>
          </Tooltip>
        </Stack>
        {reportMsg ? (
          <Alert
            severity={reportMsg.severity}
            onClose={() => setReportMsg(null)}
            sx={{ mt: 1.5 }}
          >
            {reportMsg.text}
          </Alert>
        ) : null}
      </Paper>

      <Paper variant="outlined" sx={{ p: { xs: 2, sm: 3 }, borderRadius: 2 }}>
        <Typography
          variant="caption"
          sx={{
            color: 'text.secondary',
            fontWeight: 700,
            letterSpacing: '0.08em',
            textTransform: 'uppercase',
            display: 'block',
            mb: 2,
          }}
        >
          Responses
        </Typography>
        {valueEntries.length === 0 ? (
          <Typography variant="body2" sx={{ color: 'text.secondary' }}>
            No fields recorded.
          </Typography>
        ) : (
          <Stack divider={<Divider />} spacing={0}>
            {valueEntries.map(([path, v]) => (
              <Box key={path} sx={{ py: 1.5 }}>
                <Typography
                  variant="caption"
                  sx={{ color: 'text.secondary', fontWeight: 600 }}
                >
                  {labelByPath[path] || path}
                </Typography>
                <Box sx={{ mt: 0.5 }}>{renderValue(v, labelByPath, path)}</Box>
              </Box>
            ))}
          </Stack>
        )}
      </Paper>

      {overrideError ? (
        <Alert
          severity="error"
          sx={{ mt: 2 }}
          onClose={() => setOverrideError(null)}
        >
          {overrideError}
        </Alert>
      ) : null}

      {attachments.length > 0 ? (
        <Paper
          variant="outlined"
          sx={{ p: { xs: 2, sm: 3 }, borderRadius: 2, mt: 2 }}
        >
          <Typography
            variant="caption"
            sx={{
              color: 'text.secondary',
              fontWeight: 700,
              letterSpacing: '0.08em',
              textTransform: 'uppercase',
              display: 'block',
              mb: 2,
            }}
          >
            Attachments
          </Typography>
          <Stack spacing={1}>
            {attachments.map((att, idx) => {
              // Attachments embed + open by default; only an explicit
              // `infected` (quarantined) mark blocks them. The Defender scan
              // sweep (backend/workers/attachmentScanWorker.js) flips files to
              // `clean` or `infected` when it is on; `pending` stays a normal,
              // openable state and does NOT gate the report.
              const quarantined = att.scanStatus === 'infected';
              const safeToOpen = !quarantined;
              return (
                <Stack
                  key={`${att.blobUrl}-${idx}`}
                  direction="row"
                  spacing={1.25}
                  alignItems="center"
                  sx={{
                    py: 0.75,
                    px: 1,
                    border: '1px solid',
                    borderColor: 'divider',
                    borderRadius: 1,
                  }}
                >
                  <AttachFileOutlinedIcon
                    fontSize="small"
                    sx={{ color: 'text.secondary' }}
                  />
                  <Box sx={{ minWidth: 0, flex: 1 }}>
                    <Typography
                      variant="body2"
                      sx={{
                        fontWeight: 600,
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      {att.originalName}
                    </Typography>
                    <Typography
                      variant="caption"
                      sx={{ color: 'text.secondary' }}
                    >
                      {att.mimeType} · {Math.round((att.sizeBytes || 0) / 1024)}{' '}
                      KB
                      {att.fieldId ? ` · field: ${att.fieldId}` : ''}
                    </Typography>
                  </Box>
                  {quarantined ? (
                    <Chip
                      size="small"
                      label="quarantined"
                      color="error"
                      variant="outlined"
                      sx={{ textTransform: 'capitalize' }}
                    />
                  ) : null}
                  {att.scanStatus === 'clean' ? (
                    <Chip
                      size="small"
                      label="Scanned"
                      color="success"
                      variant="outlined"
                    />
                  ) : null}
                  {safeToOpen ? (
                    <MuiLink
                      component="a"
                      href={att.blobUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      variant="caption"
                      sx={{ flexShrink: 0 }}
                    >
                      Open
                    </MuiLink>
                  ) : null}
                  {/* Quarantine excludes an unsafe file from the report;
                      Restore puts it back. Backend gates by role
                      (businessadmin/manager) - a 403 surfaces via
                      `overrideError` above. The Defender sweep sets these
                      automatically when on; this manual action is the
                      override and it's optional: files embed by default. */}
                  {quarantined ? (
                    <Tooltip title="Restore: allow this attachment in the report again">
                      <Button
                        size="small"
                        variant="outlined"
                        color="success"
                        startIcon={
                          overrideBusyFor === att.blobUrl ? (
                            <CircularProgress size={12} />
                          ) : (
                            <CheckOutlinedIcon fontSize="small" />
                          )
                        }
                        disabled={overrideBusyFor === att.blobUrl}
                        onClick={() => handleOverride(att, 'clean')}
                        sx={{ minWidth: 0, textTransform: 'none', flexShrink: 0 }}
                      >
                        Restore
                      </Button>
                    </Tooltip>
                  ) : (
                    <Tooltip title="Quarantine: exclude this attachment from the report">
                      <Button
                        size="small"
                        variant="outlined"
                        color="error"
                        startIcon={
                          overrideBusyFor === att.blobUrl ? (
                            <CircularProgress size={12} />
                          ) : (
                            <BlockOutlinedIcon fontSize="small" />
                          )
                        }
                        disabled={overrideBusyFor === att.blobUrl}
                        onClick={() => handleOverride(att, 'infected')}
                        sx={{ minWidth: 0, textTransform: 'none', flexShrink: 0 }}
                      >
                        Quarantine
                      </Button>
                    </Tooltip>
                  )}
                </Stack>
              );
            })}
          </Stack>
        </Paper>
      ) : null}
    </Box>
  );
}
